import React from "react";
import {
  Box,
  Typography,
  Avatar,
  Popover,
  Card,
  Tooltip,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { Logout, PersonOutline } from "@mui/icons-material";
import LogOutArrows from "../../assets/icons/log-out.svg";

const ProfileSection = ({ open, loggedInUser, logout, navigate }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleLogout = () => {
    navigate("/login", { replace: true });
    logout();
  };

  const handleClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const popoverOpen = Boolean(anchorEl);
  const id = open ? "user-popover" : undefined;

  if (open && loggedInUser !== undefined) {
    return (
      <Card
        sx={{
          display: "flex",
          position: "relative",
          backgroundColor: theme.palette.background.primary,
          border: `1px solid ${theme.palette.border.tertiary}`,
          padding: theme.customSpacing.lg,
          margin: `0 ${theme.customSpacing.lg}`,
          alignItems: "center",
        }}
      >
        {loggedInUser && loggedInUser.profile_picture ? (
          <Avatar src={loggedInUser.profile_picture}></Avatar>
        ) : (
          <Avatar
            sx={{
              backgroundColor: theme.palette.background.button_primary,
              color: theme.palette.foreground.button_primary,
            }}
          >
            <PersonOutline />
          </Avatar>
        )}

        <Box sx={{ marginLeft: theme.customSpacing.md, lineHeight: "0.5" }}>
          <Typography
            sx={{
              color: theme.palette.text.primary,
              fontSize: "12px",
              fontWeight: theme.typography.fontWeightMedium,
            }}
          >
            {loggedInUser?.first_name} {loggedInUser.last_name}
          </Typography>
          <Typography
            sx={{
              color: theme.palette.text.tertiary,
              fontSize: "11px",
              fontWeight: theme.typography.fontWeightLight,
            }}
          >
            {loggedInUser.email}
          </Typography>
        </Box>
        <Tooltip title="Logout" arrow>
          <Box
            onClick={handleClick}
            sx={{
              position: "absolute",
              right: theme.customSpacing.sm,
              top: "15px",
              cursor: "pointer",
            }}
          >
            <img src={LogOutArrows} alt="Logout" />
          </Box>
        </Tooltip>

        <Popover
          id="logout-popover"
          open={popoverOpen}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          slotProps={{
            paper: {
              sx: {
                p: theme.customSpacing.lg,
                width: "264px",
                backgroundColor: theme.palette.background.primary,
                border: `1px solid ${theme.palette.background.button_primary}`,
                borderRadius: theme.shape.radius.md,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: theme.customSpacing.sm,
              },
            },
          }}
        >
          <Box
            onClick={handleLogout} // ✅ only this click logs the user out
            sx={{
              display: "flex",
              alignItems: "center",
              gap: theme.customSpacing.sm,
              cursor: "pointer",
            }}
          >
            <Logout
              sx={{
                color: theme.palette.background.button_primary,
                transform: "rotate(180deg)",
                fontSize: "20px",
              }}
            />
            <Typography
              sx={{
                color: theme.palette.text.primary,
                textTransform: "none",
                fontWeight: theme.typography.fontWeightMedium,
                "&:hover": { backgroundColor: "transparent" },
              }}
            >
              Sign Out
            </Typography>
          </Box>
        </Popover>
      </Card>
    );
  }

  return (
    <>
      {loggedInUser && loggedInUser.profile_picture ? (
        <Avatar
          aria-describedby={id}
          src={loggedInUser.profile_picture}
          onClick={handleClick}
          id="sidemenu-profile-pic"
          sx={{
            width: 40,
            height: 40,
            objectFit: "cover",
            margin: "0 auto",
          }}
        ></Avatar>
      ) : (
        <Avatar
          aria-describedby={id}
          onClick={handleClick}
          sx={{
            display: "flex",
            position: "relative",
            mx: "auto",
            alignItems: "center",
            backgroundColor: theme.palette.background.button_primary,
            color: theme.palette.foreground.button_primary,
            cursor: "pointer",
          }}
        >
          <PersonOutline />
        </Avatar>
      )}

      <Popover
        id={id}
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "center",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              p: 2,
              backgroundColor: theme.palette.foreground.button_primary,
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: theme.customSpacing.md,
            alignItems: "center",
          }}
        >
          <Box>
            <Typography
              sx={{
                color: theme.palette.text.primary,
                fontSize: "12px",
                fontWeight: theme.typography.fontWeightMedium,
                lineHeight: "1.2",
              }}
            >
              {loggedInUser.first_name} {loggedInUser.last_name}
            </Typography>
            <Typography
              sx={{
                color: theme.palette.text.tertiary,
                fontSize: "12px",
                fontWeight: theme.typography.fontWeightLight,
                lineHeight: theme.typography.textSM.lineHeight,
              }}
            >
              {loggedInUser.email}
            </Typography>
          </Box>

          <Button
            variant="text"
            color="error"
            size="small"
            onClick={handleLogout}
            disableRipple
            sx={{
              px: 1.5,
              py: 0.75,
              color: "white",
              "&:hover": { backgroundColor: "transparent" },
              "&:focus": { outline: "none", boxShadow: "none" },
            }}
          >
            <Logout
              sx={{
                color: theme.palette.background.button_primary,
                transform: "rotate(180deg)",
                fontSize: "20px",
              }}
            />
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default ProfileSection;
